<template>
	<v-card data-testid = "dashboard-operator-action-time-widget" dark outlined class = "dashboard-operator-action-time-widget fill-height">
		<v-system-bar lights-out dark class = "vue-draggable-handle" style = "cursor:move">
			<v-icon small color = "green lighten-2">mdi-timer-check-outline</v-icon>
			<span class = "white--text subtitle-2 text-uppercase ml-1 text-truncate">{{ widgetTitle }}</span>
			<v-spacer></v-spacer>
			<span v-if = "generatedAt" class = "caption grey--text text-truncate mr-2">
				{{ $ml.get('dashboard_pie_events_generated_at') }}: {{ generatedAt }}
			</span>
			<v-btn icon x-small class = "no-drag" :disabled = "loading" @click = "refresh">
				<v-icon x-small color = "white">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon x-small class = "error ml-1 no-drag" @click = "$emit('close_component', layout_id)">
				<v-icon x-small color = "white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<div class = "dashboard-operator-action-time-widget__body no-drag">
			<div class = "dashboard-operator-action-time-widget__intervals">
				<span class = "caption grey--text text-uppercase">{{ $ml.get('dashboard_operator_action_time_interval') }}</span>
				<v-btn-toggle v-model = "selectedInterval" mandatory dense dark class = "dashboard-operator-action-time-widget__interval-toggle">
					<v-btn v-for = "option in intervalOptions" :key = "option.value" x-small :value = "option.value" :disabled = "loading">
						{{ $ml.get(option.labelKey) }}
					</v-btn>
				</v-btn-toggle>
			</div>

			<v-overlay v-if = "loading && !hasData" absolute z-index = "10">
				<div class = "text-center">
					<v-progress-circular indeterminate size = "44" color = "green lighten-2"></v-progress-circular>
					<div class = "mt-3 caption grey--text">{{ $ml.get('dashboard_operator_action_time_loading') }}</div>
				</div>
			</v-overlay>

			<v-alert v-if = "unsupportedSchema" type = "warning" dense prominent class = "ma-2">
				{{ $ml.get('dashboard_pie_events_unsupported_config') }}
			</v-alert>

			<v-alert v-else-if = "error" type = "error" dense prominent class = "ma-2">
				<v-row align = "center" no-gutters>
					<v-col class = "grow">{{ error }}</v-col>
					<v-col class = "shrink">
						<v-btn small outlined color = "white" class = "ml-3" @click = "refresh">
							<v-icon small left>mdi-refresh</v-icon>
							{{ $ml.get('dashboard_pie_events_retry') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-alert>

			<div v-else-if = "!loading && !hasData" class = "dashboard-operator-action-time-widget__empty text-center">
				<v-icon large color = "grey lighten-1">mdi-timer-sand-empty</v-icon>
				<div class = "caption grey--text mt-2">{{ $ml.get('dashboard_operator_action_time_empty') }}</div>
			</div>

			<div v-else class = "dashboard-operator-action-time-widget__content">
				<div class = "dashboard-operator-action-time-widget__header">
					<div>
						<div class = "caption grey--text text-uppercase">{{ $ml.get('dashboard_operator_action_time_subtitle') }}</div>
						<div class = "caption dashboard-operator-action-time-widget__source">
							{{ $ml.get('dashboard_operator_action_time_source') }}: {{ actionTimestampSource }}
						</div>
					</div>
					<div class = "dashboard-operator-action-time-widget__objective">
						<span>{{ $ml.get('dashboard_operator_action_time_objective') }}</span>
						<strong>{{ formatSeconds(objectiveSeconds) }}</strong>
					</div>
				</div>

				<div class = "dashboard-operator-action-time-widget__stats">
					<div v-for = "stat in stats" :key = "stat.key" class = "dashboard-operator-action-time-widget__stat">
						<span>{{ stat.label }}</span>
						<strong :class = "stat.className">{{ stat.value }}</strong>
					</div>
				</div>

				<div class = "dashboard-operator-action-time-widget__daily">
					<div class = "caption grey--text text-uppercase mb-2">{{ $ml.get('dashboard_operator_action_time_daily_average') }}</div>
					<div v-if = "dailySeries.length" class = "dashboard-operator-action-time-widget__daily-chart">
						<div v-for = "point in dailySeries" :key = "point.date" class = "dashboard-operator-action-time-widget__daily-point" :title = "dailyPointTitle(point)">
							<div class = "dashboard-operator-action-time-widget__daily-bar-track">
								<span :class = "dailyPointClass(point)" :style = "{ height: dailyPointHeight(point) }"></span>
							</div>
							<div class = "dashboard-operator-action-time-widget__daily-value">{{ formatSeconds(point.averageSeconds) }}</div>
							<div class = "dashboard-operator-action-time-widget__daily-label">{{ point.label }}</div>
						</div>
					</div>
					<div v-else class = "dashboard-operator-action-time-widget__daily-empty caption grey--text">
						{{ $ml.get('dashboard_operator_action_time_daily_empty') }}
					</div>
				</div>

				<div class = "dashboard-operator-action-time-widget__distribution">
					<div class = "caption grey--text text-uppercase mb-2">{{ $ml.get('dashboard_operator_action_time_distribution') }}</div>
					<div v-for = "bucket in buckets" :key = "bucket.key" class = "dashboard-operator-action-time-widget__bucket">
						<div class = "dashboard-operator-action-time-widget__bucket-label">
							<span>{{ bucket.label }}</span>
							<strong>{{ bucket.count }} · {{ formatPercentage(bucket.percentage) }}</strong>
						</div>
						<div class = "dashboard-operator-action-time-widget__bar">
							<span :style = "{ width: bucketWidth(bucket), background: bucketColor(bucket) }"></span>
						</div>
					</div>
				</div>

				<div class = "dashboard-operator-action-time-widget__footer caption grey--text">
					<span>{{ $ml.get('dashboard_operator_action_time_total') }}: <strong class = "white--text">{{ total }}</strong></span>
					<span>{{ $ml.get('dashboard_pie_events_refresh_seconds') }}: {{ refreshSeconds }}</span>
				</div>

				<div v-if = "skippedWithoutActionTimestamp > 0" class = "dashboard-operator-action-time-widget__note caption amber--text text--lighten-2">
					<v-icon x-small color = "amber lighten-2">mdi-alert-circle-outline</v-icon>
					{{ $ml.get('dashboard_operator_action_time_skipped_note') }}: {{ skippedWithoutActionTimestamp }}
				</div>

				<div v-if = "pendingWithoutActionTimestamp > 0" class = "dashboard-operator-action-time-widget__note caption light-blue--text text--lighten-2">
					<v-icon x-small color = "light-blue lighten-2">mdi-timer-sand</v-icon>
					{{ $ml.get('dashboard_operator_action_time_pending_note') }}: {{ pendingWithoutActionTimestamp }}
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import momentz from 'moment-timezone';
import DashboardChartsService from '@/services/dashboard-charts.service.js';
import {
	DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
	DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
	buildRollingTimeRange,
} from '@/services/dashboard-pie-events.config.js';

const DEFAULT_BUCKETS = ['0_10', '10_20', '20_30', '30_60', '60_plus'];
const DEFAULT_ACTION_TIMESTAMP_SOURCE = 'Alert.datetime_confirmation';
const INTERVAL_OPTIONS = [
	{ value: 'today', labelKey: 'dashboard_pie_events_preset_today' },
	{ value: 'yesterday', labelKey: 'dashboard_pie_events_preset_yesterday' },
	{ value: 'week', labelKey: 'dashboard_pie_events_preset_week' },
	{ value: '15d', labelKey: 'dashboard_pie_events_preset_15d' },
	{ value: '30d', labelKey: 'dashboard_pie_events_preset_30d' },
];

function normalizePresetAlias(preset){
	if (preset === 'last_7d') return 'week';
	if (preset === 'last_30d') return '30d';
	if (preset === 'last_24h') return 'today';
	return preset || 'today';
}

function resolveTimezone(value){
	return value && momentz.tz.zone(value) ? value : 'America/Lima';
}

function buildOperatorActionTimeRange(preset, timezone){
	const tz = resolveTimezone(timezone);
	if (preset === 'today') {
		const now = momentz.tz(tz);
		return {
			preset,
			custom: { from: now.clone().startOf('day').toISOString(), to: now.toISOString(), tz },
		};
	}
	if (preset === 'yesterday') {
		const yesterday = momentz.tz(tz).subtract(1, 'day');
		return {
			preset,
			custom: { from: yesterday.clone().startOf('day').toISOString(), to: yesterday.clone().endOf('day').toISOString(), tz },
		};
	}
	return buildRollingTimeRange(preset, tz);
}

export default {
	name : 'dashboard_operator_action_time_widget',
	props : ['layout', 'layout_id', 'hosts', 'info_to_component', 'layout_end'],
	data(){
		return {
			metricData : this.emptyMetricData(),
			loading : false,
			error : null,
			generatedAt : null,
			refreshTimer : null,
			selectedInterval : this.getInitialInterval(),
		};
	},
	computed : {
		intervalOptions(){
			return INTERVAL_OPTIONS;
		},
		streamData(){
			return this.info_to_component || this.hosts || {};
		},
		timezone(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.time && config.time.custom && config.time.custom.tz ? config.time.custom.tz : 'America/Lima';
		},
		requestStreamData(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return Object.assign({}, this.streamData, {
				config: Object.assign({}, config, {
					time: buildOperatorActionTimeRange(this.selectedInterval, this.timezone),
				}),
			});
		},
		unsupportedSchema(){
			return !this.streamData || this.streamData.schemaVersion !== DASHBOARD_PIE_EVENTS_SCHEMA_VERSION;
		},
		widgetTitle(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const titleKey = (this.streamData && this.streamData.titleKey) || config.titleKey;
			if (titleKey) return this.$ml.get(titleKey);
			return (this.streamData && this.streamData.title) || config.name || config.title || this.$ml.get('dashboard_pie_events_preset_tiempo_accion_operador');
		},
		refreshSeconds(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const value = Number(config.refreshSeconds || this.streamData.refreshSeconds || DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS);
			return value > 0 ? value : DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS;
		},
		hasData(){
			return Number(this.metricData.total || 0) > 0 || this.buckets.some(bucket => Number(bucket.count || 0) > 0);
		},
		objectiveSeconds(){
			return this.metricData.objectiveSeconds || 30;
		},
		actionTimestampSource(){
			return this.metricData.actionTimestampSource || DEFAULT_ACTION_TIMESTAMP_SOURCE;
		},
		pendingWithoutActionTimestamp(){
			return this.metricData.pendingWithoutActionTimestamp || 0;
		},
		skippedWithoutActionTimestamp(){
			return this.metricData.skippedWithoutActionTimestamp || 0;
		},
		total(){
			return this.metricData.total || 0;
		},
		buckets(){
			return this.metricData.buckets || [];
		},
		dailySeries(){
			return this.metricData.dailySeries || [];
		},
		maxDailyAverageSeconds(){
			const values = this.dailySeries
				.map(point => this.normalizeNumber(point.averageSeconds, null))
				.filter(value => value !== null && value >= 0);
			return Math.max(this.objectiveSeconds || 0, values.length ? Math.max.apply(null, values) : 0, 1);
		},
		stats(){
			return [
				{ key: 'average', label: this.$ml.get('dashboard_operator_action_time_average'), value: this.formatSeconds(this.metricData.averageSeconds) },
				{ key: 'p95', label: this.$ml.get('dashboard_operator_action_time_p95'), value: this.formatSeconds(this.metricData.p95Seconds) },
				{ key: 'best', label: this.$ml.get('dashboard_operator_action_time_best'), value: this.formatSeconds(this.metricData.bestSeconds), className: 'success--text text--lighten-2' },
				{ key: 'within', label: this.$ml.get('dashboard_operator_action_time_within_objective'), value: this.formatPercentage(this.metricData.withinObjectivePercentage), className: this.metricData.withinObjectivePercentage >= 80 ? 'success--text text--lighten-2' : 'orange--text text--lighten-2' },
			];
		},
	},
	watch : {
		streamData : {
			handler(){
				this.resetWidget();
			},
			deep : true,
		},
		selectedInterval(){
			this.refresh();
		},
	},
	mounted(){
		this.loadInitialData();
	},
	beforeDestroy(){
		this.clearRefreshTimer();
	},
	methods : {
		emptyMetricData(){
			return {
				metric: 'operator_action_time',
				unit: 'seconds',
				objectiveSeconds: 30,
				averageSeconds: null,
				p95Seconds: null,
				bestSeconds: null,
				withinObjectivePercentage: null,
				total: 0,
				pendingWithoutActionTimestamp: 0,
				skippedWithoutActionTimestamp: 0,
				actionTimestampSource: DEFAULT_ACTION_TIMESTAMP_SOURCE,
				buckets: [],
				dailySeries: [],
			};
		},
		getInitialInterval(){
			const config = this.info_to_component && this.info_to_component.config ? this.info_to_component.config : {};
			const preset = normalizePresetAlias(config.time && config.time.preset ? config.time.preset : 'today');
			return INTERVAL_OPTIONS.some(option => option.value === preset) ? preset : 'today';
		},
		loadInitialData(){
			if (this.unsupportedSchema) return;
			if (this.streamData.preview) this.metricData = this.normalizeMetricData(this.streamData.preview);
			this.refresh();
			this.startRefreshTimer();
		},
		resetWidget(){
			this.clearRefreshTimer();
			this.error = null;
			this.generatedAt = null;
			this.metricData = this.emptyMetricData();
			this.selectedInterval = this.getInitialInterval();
			this.loadInitialData();
		},
		refresh(){
			if (this.unsupportedSchema || this.loading) return;
			this.loading = true;
			this.error = null;

			DashboardChartsService.getPieEventsData(this.requestStreamData).then((response) => {
				if (response && (response.isAxiosError || response.response)) {
					this.error = this.$ml.get('dashboard_operator_action_time_error');
					return;
				}
				const data = this.unwrapResponse(response);
				this.metricData = this.normalizeMetricData(data);
				this.generatedAt = data && (data.generatedAt || data.generated_at) ? (data.generatedAt || data.generated_at) : null;
				this.restartRefreshTimer(data && (data.nextRefreshSeconds || data.next_refresh_seconds));
			}).catch(() => {
				this.error = this.$ml.get('dashboard_operator_action_time_error');
			}).finally(() => {
				this.loading = false;
			});
		},
		unwrapResponse(response){
			if (!response) return null;
			if (response.data && response.data.data) return response.data.data;
			if (response.data) return response.data;
			return response;
		},
		normalizeMetricData(data){
			const source = data && data.chart ? data.chart : data;
			if (!source) return this.emptyMetricData();
			const normalizedBuckets = this.normalizeBuckets(source.buckets);
			const normalizedDailySeries = this.normalizeDailySeries(source.dailySeries || source.daily_series || source.dailyAverageSeries || source.daily_average_series);

			return Object.assign(this.emptyMetricData(), source, {
				objectiveSeconds: this.normalizeNumber(this.pickValue(source, 'objectiveSeconds', 'objective_seconds'), 30),
				averageSeconds: this.normalizeNumber(this.pickValue(source, 'averageSeconds', 'average_seconds'), null),
				p95Seconds: this.normalizeNumber(this.pickValue(source, 'p95Seconds', 'p95_seconds'), null),
				bestSeconds: this.normalizeNumber(this.pickValue(source, 'bestSeconds', 'best_seconds'), null),
				withinObjectivePercentage: this.normalizeNumber(this.pickValue(source, 'withinObjectivePercentage', 'within_objective_percentage'), null),
				total: this.normalizeNumber(source.total, 0),
				pendingWithoutActionTimestamp: this.normalizeNumber(this.pickValue(source, 'pendingWithoutActionTimestamp', 'pending_without_action_timestamp'), 0),
				skippedWithoutActionTimestamp: this.normalizeNumber(this.pickValue(source, 'skippedWithoutActionTimestamp', 'skipped_without_action_timestamp'), 0),
				actionTimestampSource: source.actionTimestampSource || source.action_timestamp_source || DEFAULT_ACTION_TIMESTAMP_SOURCE,
				buckets: normalizedBuckets,
				dailySeries: normalizedDailySeries,
			});
		},
		pickValue(source, camelKey, snakeKey){
			if (source && source[camelKey] !== undefined && source[camelKey] !== null) return source[camelKey];
			return source ? source[snakeKey] : undefined;
		},
		normalizeBuckets(buckets){
			if (!Array.isArray(buckets)) return [];
			return buckets.map((bucket, index) => ({
				key: bucket.key || DEFAULT_BUCKETS[index] || `${index}`,
				label: bucket.label || bucket.key || `${index + 1}`,
				count: this.normalizeNumber(bucket.count, 0),
				percentage: this.normalizeNumber(bucket.percentage || bucket.percent, 0),
			}));
		},
		normalizeDailySeries(series){
			if (!Array.isArray(series)) return [];
			return series.map((point, index) => {
				const date = point.date || point.day || point.key || `${index}`;
				return {
					date,
					label: point.label || date,
					averageSeconds: this.normalizeNumber(this.pickValue(point, 'averageSeconds', 'average_seconds'), null),
					count: this.normalizeNumber(point.count, 0),
				};
			});
		},
		normalizeNumber(value, fallback){
			if (value === undefined || value === null || value === '') return fallback;
			const numeric = Number(value);
			return Number.isFinite(numeric) ? numeric : fallback;
		},
		formatSeconds(value){
			if (value === undefined || value === null || value === '') return '—';
			const numeric = Number(value);
			if (!Number.isFinite(numeric)) return '—';
			const abs = Math.abs(numeric);
			if (abs >= 86400) return `${this.formatCompactNumber(numeric / 86400)}d`;
			if (abs >= 3600) return `${this.formatCompactNumber(numeric / 3600)}h`;
			if (abs >= 60) return `${this.formatCompactNumber(numeric / 60)}m`;
			return `${this.formatCompactNumber(numeric)}s`;
		},
		formatCompactNumber(value){
			if (!Number.isFinite(value)) return '—';
			return value % 1 === 0 ? `${value}` : value.toFixed(1);
		},
		formatPercentage(value){
			if (value === undefined || value === null || value === '') return '—';
			const numeric = Number(value);
			if (!Number.isFinite(numeric)) return '—';
			return `${numeric % 1 === 0 ? numeric : numeric.toFixed(1)}%`;
		},
		bucketWidth(bucket){
			const percentage = this.normalizeNumber(bucket && bucket.percentage, 0);
			return `${Math.max(0, Math.min(100, percentage))}%`;
		},
		bucketColor(bucket){
			if (!bucket || bucket.key === '60_plus') return '#EF5350';
			if (bucket.key === '30_60') return '#FFA726';
			return '#66BB6A';
		},
		dailyPointHeight(point){
			const averageSeconds = this.normalizeNumber(point && point.averageSeconds, null);
			if (averageSeconds === null || this.normalizeNumber(point && point.count, 0) <= 0) return '0%';
			const percentage = Math.max(0, Math.min(100, (averageSeconds / this.maxDailyAverageSeconds) * 100));
			return `${Math.max(6, percentage)}%`;
		},
		dailyPointClass(point){
			const averageSeconds = this.normalizeNumber(point && point.averageSeconds, null);
			return averageSeconds !== null && averageSeconds > this.objectiveSeconds
				? 'dashboard-operator-action-time-widget__daily-bar--warning'
				: 'dashboard-operator-action-time-widget__daily-bar--ok';
		},
		dailyPointTitle(point){
			return `${point.label}: ${this.formatSeconds(point.averageSeconds)} · ${this.$ml.get('dashboard_operator_action_time_total')}: ${point.count}`;
		},
		startRefreshTimer(seconds){
			this.clearRefreshTimer();
			const refreshSeconds = Number(seconds || this.refreshSeconds);
			this.refreshTimer = setInterval(() => this.refresh(), (refreshSeconds > 0 ? refreshSeconds : this.refreshSeconds) * 1000);
		},
		restartRefreshTimer(seconds){
			this.startRefreshTimer(seconds);
		},
		clearRefreshTimer(){
			if (this.refreshTimer) {
				clearInterval(this.refreshTimer);
				this.refreshTimer = null;
			}
		},
	},
};
</script>

<style scoped>
.dashboard-operator-action-time-widget { overflow: hidden; background: #14171F; }
.dashboard-operator-action-time-widget__body { height: calc(100% - 24px); padding: 12px; position: relative; overflow: auto; }
.dashboard-operator-action-time-widget__intervals { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 10px; }
.dashboard-operator-action-time-widget__interval-toggle { border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 8px; overflow: hidden; }
.dashboard-operator-action-time-widget__content { min-height: 100%; display: flex; flex-direction: column; gap: 12px; }
.dashboard-operator-action-time-widget__header { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.dashboard-operator-action-time-widget__source { color: rgba(255, 255, 255, 0.58); }
.dashboard-operator-action-time-widget__objective { min-width: 96px; padding: 8px 10px; border-radius: 10px; background: rgba(102, 187, 106, 0.14); border: 1px solid rgba(102, 187, 106, 0.35); text-align: right; }
.dashboard-operator-action-time-widget__objective span { display: block; font-size: 11px; color: rgba(255, 255, 255, 0.68); text-transform: uppercase; }
.dashboard-operator-action-time-widget__objective strong { color: #A5D6A7; font-size: 20px; }
.dashboard-operator-action-time-widget__stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.dashboard-operator-action-time-widget__stat { padding: 10px; border-radius: 10px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.08); }
.dashboard-operator-action-time-widget__stat span { display: block; font-size: 11px; color: rgba(255, 255, 255, 0.62); text-transform: uppercase; }
.dashboard-operator-action-time-widget__stat strong { display: block; margin-top: 4px; color: #FFFFFF; font-size: 24px; line-height: 1; font-variant-numeric: tabular-nums; }
.dashboard-operator-action-time-widget__daily { padding: 10px; border-radius: 12px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); }
.dashboard-operator-action-time-widget__daily-chart { min-height: 138px; display: flex; align-items: stretch; gap: 6px; overflow-x: auto; padding-bottom: 2px; }
.dashboard-operator-action-time-widget__daily-point { min-width: 38px; flex: 1 0 38px; display: flex; flex-direction: column; align-items: center; gap: 4px; color: rgba(255, 255, 255, 0.72); }
.dashboard-operator-action-time-widget__daily-bar-track { width: 100%; height: 82px; display: flex; align-items: flex-end; border-radius: 8px; background: rgba(255, 255, 255, 0.08); overflow: hidden; }
.dashboard-operator-action-time-widget__daily-bar-track span { display: block; width: 100%; border-radius: 8px 8px 0 0; transition: height 180ms ease; }
.dashboard-operator-action-time-widget__daily-bar--ok { background: linear-gradient(180deg, #A5D6A7 0%, #43A047 100%); }
.dashboard-operator-action-time-widget__daily-bar--warning { background: linear-gradient(180deg, #FFCC80 0%, #FB8C00 100%); }
.dashboard-operator-action-time-widget__daily-value { min-height: 14px; max-width: 52px; font-size: 11px; line-height: 1; color: #FFFFFF; font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dashboard-operator-action-time-widget__daily-label { font-size: 10px; line-height: 1; color: rgba(255, 255, 255, 0.54); white-space: nowrap; }
.dashboard-operator-action-time-widget__daily-empty { padding: 16px 0; text-align: center; }
.dashboard-operator-action-time-widget__distribution { flex: 1; min-height: 0; }
.dashboard-operator-action-time-widget__bucket { margin-bottom: 8px; }
.dashboard-operator-action-time-widget__bucket-label { display: flex; justify-content: space-between; align-items: center; color: rgba(255, 255, 255, 0.78); font-size: 12px; }
.dashboard-operator-action-time-widget__bucket-label strong { color: #FFFFFF; font-weight: 600; }
.dashboard-operator-action-time-widget__bar { height: 8px; margin-top: 4px; border-radius: 999px; background: rgba(255, 255, 255, 0.10); overflow: hidden; }
.dashboard-operator-action-time-widget__bar span { display: block; height: 100%; border-radius: inherit; }
.dashboard-operator-action-time-widget__footer { display: flex; justify-content: space-between; gap: 10px; }
.dashboard-operator-action-time-widget__note { display: flex; align-items: center; gap: 4px; }
.dashboard-operator-action-time-widget__empty { display: flex; height: 100%; min-height: 180px; align-items: center; justify-content: center; flex-direction: column; }
@media (max-width: 960px) { .dashboard-operator-action-time-widget__stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
